import { NameAnd } from "@me8eon/record_utils";
import { safeArray } from "@me8eon/arrays";
import { ErrorsOr, flattenArrayOfErrorsOr, isErrors, Value } from "./error.monad";


export type Severity = "debug" | "info" | "warn" | "error"

export type LogOptions = {
    whatHappened: string
    params?: any
    severity?: Severity
}
export type NormalisedLogOptions = Required<LogOptions>
export type LogOptionOrString = LogOptions | string
export type LogOptionInResult = LogOptionOrString | LogOptionOrString[]
export type LogAnd<T> = T & { log?: LogOptionInResult }

export function paramsFrom<T>(x: LogAnd<T>) {
    return (x.log as any).params;
}

export type NameAndLogAnd<T> = T & { logs: NameAnd<NormalisedLogOptions[]> }

export function normaliseLogOptions(l?: LogOptionInResult): NormalisedLogOptions[] {
    if (!l) return [];
    const logsArray = Array.isArray(l) ? l : [l];

    return logsArray.map((entry: (LogOptions | string)) => {
        return typeof entry === "string"
            ? { whatHappened: entry, severity: "info", params: [] }
            : { whatHappened: entry.whatHappened, severity: entry.severity || "info", params: safeArray(entry.params) };
    });
}

export function mapLog<T, T1>(ts: LogAnd<ErrorsOr<T>>, fn: (t: T) => T1): LogAnd<ErrorsOr<T1>> {
    if ("errors" in ts) return ts;
    return { ...ts, value: fn(ts.value) };
}

export async function mapLogK<T, T1>(ts: LogAnd<ErrorsOr<T>>, fn: (t: T) => Promise<T1>): Promise<LogAnd<ErrorsOr<T1>>> {
    if ("errors" in ts) return ts;
    const value = await fn(ts.value);
    return { ...ts, value };
}

export async function flatMapLogK<T, T1>(ts: LogAnd<ErrorsOr<T>>, fn: (t: T) => Promise<LogAnd<ErrorsOr<T1>>>): Promise<LogAnd<ErrorsOr<T1>>> {
    if ("errors" in ts) return ts;
    const log: LogOptionOrString[] = [...safeArray(ts.log)];
    const result = await fn(ts.value);
    log.push(...safeArray(result.log));
    return { ...result, log };

}

export function flattenLogsAndErrors<T>(inp: LogAnd<ErrorsOr<T>>[]): LogAnd<ErrorsOr<T[]>> {
    const log = inp?.map(l => l.log).filter(log => log !== undefined) as LogOptionOrString[];
    const withoutLogs = flattenArrayOfErrorsOr(inp);
    return { ...withoutLogs, log };
}

export function recoverErrors<T>(inp: LogAnd<ErrorsOr<T>>[], fn: (e: string[]) => T): LogAnd<Value<T[]>> {
    const log: LogOptionOrString[] = inp.flatMap(i => safeArray(i.log));
    const value = inp.map(i => isErrors(i) ? fn(i.errors) : i.value);
    return { value, log };
}